import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useProfile } from '../context/ProfileContext';
import ProfileForm from '../components/profile/ProfileForm';
import Loader from '../components/common/Loader';
import ResumeUpload from '../components/tailor/ResumeUpload';
import Modal from '../components/common/Modal';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaUser,
  FaGraduationCap,
  FaTools,
  FaBriefcase,
  FaCode,
  FaPuzzlePiece,
  FaMagic,
  FaCheckCircle,
  FaExclamationTriangle,
  FaChevronRight,
  FaChevronLeft,
  FaArrowRight
} from 'react-icons/fa';
import './Profile.css';
import ResumeUploader from '../components/profile/ResumeUploader';

const Profile = () => {
  const { profileData, loading, error, refreshProfile } = useProfile();
  const [activeSection, setActiveSection] = useState('personal');
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [completionStatus, setCompletionStatus] = useState({});
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    document.title += ' - Profile';
    // This will clean up the title when component unmounts
    return () => {
      document.title = 'CVance'; // Default title
    };
  }, []);

  // Profile sections with metadata
  const profileSections = [
    { id: 'personal', icon: <FaUser />, label: 'Personal', description: 'Basic information about yourself' },
    { id: 'education', icon: <FaGraduationCap />, label: 'Education', description: 'Your academic background' },
    { id: 'skills', icon: <FaTools />, label: 'Skills', description: 'Technical and soft skills' },
    { id: 'experience', icon: <FaBriefcase />, label: 'Experience', description: 'Work history and positions' },
    { id: 'projects', icon: <FaCode />, label: 'Projects', description: 'Showcase your work' },
    { id: 'custom', icon: <FaPuzzlePiece />, label: 'Custom', description: 'Other relevant sections' }
  ];

  // Calculate profile completion percentage
  useEffect(() => {
    if (profileData) {
      const sections = {
        personal: !!profileData.user?.first_name && !!profileData.user?.last_name && !!profileData.user?.phone_number && !!profileData.user?.email,
        education: profileData.education?.length > 0,
        skills: profileData.skill_categories?.length > 0,
        experience: profileData.experiences?.length > 0,
        projects: profileData.projects?.length > 0,
        // certificates: profileData.certificates?.length > 0,
        // extra: profileData?.extracurricular_activities?.length > 0,
        custom: profileData.custom_sections?.length > 0
      };

      setCompletionStatus(sections);

      // Update active step based on sections that need completion
      const sectionIds = Object.keys(sections);
      const nextIncompleteIndex = sectionIds.findIndex(id => !sections[id]);
      if (nextIncompleteIndex !== -1) {
        setActiveStep(nextIncompleteIndex);
        setActiveSection(profileSections[nextIncompleteIndex].id)
      }
    }
  }, [profileData]);

  // Handle section change
  const handleSectionChange = (sectionId) => {
    setActiveSection(sectionId);
    const index = profileSections.findIndex(section => section.id === sectionId);
    if (index !== -1) {
      setActiveStep(index);
    }
  };

  const handleSectionComplete = (section, content) => {
    setCompletionStatus({
      ...completionStatus,
      [section]: content?.length > 0
    });
  };


  // Navigate to next/previous step
  const goToNextStep = () => {
    const nextStep = Math.min(activeStep + 1, profileSections.length - 1);
    setActiveStep(nextStep);
    setActiveSection(profileSections[nextStep].id);
  };

  const goToPrevStep = () => {
    const prevStep = Math.max(activeStep - 1, 0);
    setActiveStep(prevStep);
    setActiveSection(profileSections[prevStep].id);
  };

  // Handle resume upload
  const handleResumeUpload = (file) => {
    console.log('Resume uploaded:', file);
  };

  const handleProfileUpdate = () => {
    // Refresh the profile data
    refreshProfile();
  };

  // Handle resume parsing
  const handleResumeParse = async (file) => {
    try {
      setIsProcessing(true);
      await new Promise(resolve => setTimeout(resolve, 2000));
      console.log('Resume parsed:', file);
      await refreshProfile();
      setUploadModalOpen(false);
    } catch (err) {
      console.error('Error parsing resume:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  // Determine if profile is complete enough to tailor resumes
  const canTailorResume = () => {
    return (
      profileData?.education?.length > 0 &&
      profileData?.skill_categories?.length > 0 &&
      profileData?.experiences?.length > 0
    );
  };

  if (loading) {
    return (
      <div className="loader-container">
        <Loader text="Loading your profile..." />
        <p className="loader-subtext">Preparing your professional journey...</p>
      </div>
    );
  }

  return (
    <motion.div
      className="app-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >

      {/* Main Content Area */}
      <div className={`main-content ${sidebarCollapsed ? 'expanded' : ''}`}>
        <div className="content-wrapper">
          {/* Header with Actions */}
          <motion.div
            className="page-header"
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
          >
            <div className="header-content">
              <h1>Profile Builder</h1>
              <p className="header-subtitle">
                Create a comprehensive profile to power your tailored resumes
              </p>
            </div>
            <div className="header-actions">
              {/* <motion.button 
                className="action-button upload"
                onClick={() => setUploadModalOpen(true)}
                whileHover={{ scale: 1.03, y: -2 }}
                whileTap={{ scale: 0.97 }}
              >
                <FaCloudUploadAlt /> Import Resume
              </motion.button> */}
              <ResumeUploader onProfileUpdate={handleProfileUpdate} />
              <motion.button
                className={`action-button tailor ${!canTailorResume() ? 'disabled' : ''}`}
                disabled={!canTailorResume()}
                title={!canTailorResume() ? "Complete your profile to enable resume tailoring" : "Tailor your resume"}
                whileHover={canTailorResume() ? { scale: 1.03, y: -2 } : {}}
                whileTap={canTailorResume() ? { scale: 0.97 } : {}}
                onClick={() => navigate('/tailor')}
              >
                <FaMagic /> Tailor Resume
              </motion.button>
            </div>
          </motion.div>

          {/* Visual Progress Indicator */}
          <motion.div
            className="progress-timeline"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="timeline-track">
              {profileSections.map((section, index) => (
                <motion.div
                  key={section.id}
                  className={`timeline-node ${index === activeStep ? 'active' : ''} ${completionStatus[section.id] ? 'completed' : ''}`}
                  onClick={() => handleSectionChange(section.id)}
                  whileHover={{ scale: 1.15 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <div className="node-icon">
                    {completionStatus[section.id] ? <FaCheckCircle /> : section.icon}
                  </div>
                  <div className="node-label">{section.label}</div>
                </motion.div>
              ))}
              <div className="timeline-progress" style={{ width: `${(activeStep / (profileSections.length - 1)) * 100}%` }}></div>
            </div>
          </motion.div>

          {/* Main Content Cards */}
          <div className="content-cards">
            {/* Section Info Card */}
            <motion.div
              className="section-info-card"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
            >
              <div className="card-header-profile">
                <div className="section-icon">
                  {profileSections[activeStep].icon}
                </div>
                <div className="section-title-profile">
                  <h2>{profileSections[activeStep].label}</h2>
                  <p>{profileSections[activeStep].description}</p>
                </div>
              </div>

              <div className="section-actions">
                <button
                  className="step-button prev"
                  onClick={goToPrevStep}
                  disabled={activeStep === 0}
                >
                  <FaChevronLeft /> Previous
                </button>
                <button
                  className="step-button next"
                  onClick={goToNextStep}
                  disabled={activeStep === profileSections.length - 1}
                >
                  Next <FaChevronRight />
                </button>
              </div>

              {/* Tips Card */}
              <div className="tips-card">
                <h4>Tips for this section:</h4>
                <ul>
                  {activeSection === 'personal' && (
                    <>
                      <li>Use a professional email address</li>
                      <li>Include your LinkedIn profile if available</li>
                    </>
                  )}
                  {activeSection === 'education' && (
                    <>
                      <li>List your most recent education first</li>
                      <li>Include relevant coursework and achievements</li>
                    </>
                  )}
                  {activeSection === 'skills' && (
                    <>
                      <li>Organize skills into categories</li>
                      <li>Include both technical and soft skills</li>
                    </>
                  )}
                  {activeSection === 'experience' && (
                    <>
                      <li>Use action verbs to describe achievements</li>
                      <li>Quantify results where possible</li>
                    </>
                  )}
                  {activeSection === 'projects' && (
                    <>
                      <li>Highlight your most impressive projects</li>
                      <li>Include links to project demos if available</li>
                    </>
                  )}
                  {activeSection === 'certificates' && (
                    <>
                      <li>Include the year of certification</li>
                      <li>Mention issuing organization</li>
                    </>
                  )}
                  {activeSection === 'extra' && (
                    <>
                      <li>Focus on activities that demonstrate leadership</li>
                      <li>Highlight community involvement</li>
                    </>
                  )}
                  {activeSection === 'custom' && (
                    <>
                      <li>Use custom sections for unique qualifications</li>
                      <li>Consider adding languages, publications, etc.</li>
                    </>
                  )}
                </ul>
              </div>
            </motion.div>

            {/* Form Content Card */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeSection}
                className="form-content-card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <ProfileForm initialSection={activeSection} setSection={setActiveSection} profileSection={profileSections[activeStep]} setCompletion={handleSectionComplete} />
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Quick Navigation Cards */}
          <motion.div
            className="quick-nav-cards"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            {profileSections.map((section) => (
              <motion.div
                key={section.id}
                className={`quick-nav-card ${activeSection === section.id ? 'active' : ''} ${completionStatus[section.id] ? 'completed' : ''}`}
                onClick={() => handleSectionChange(section.id)}
                whileHover={{ y: -5, boxShadow: '0 8px 16px rgba(0,0,0,0.1)' }}
                whileTap={{ y: 0, boxShadow: '0 4px 8px rgba(0,0,0,0.1)' }}
              >
                <div className="card-icon">
                  {section.icon}
                  {completionStatus[section.id] && <div className="completion-badge"><FaCheckCircle /></div>}
                </div>
                <div className="card-label">{section.label}</div>
                {activeSection === section.id && <div className="active-indicator"><FaArrowRight /></div>}
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Resume Upload Modal */}
      <AnimatePresence>
        {uploadModalOpen && (
          <Modal
            isOpen={uploadModalOpen}
            onClose={() => setUploadModalOpen(false)}
            title="Import Your Resume"
            size="medium"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <ResumeUpload
                onUpload={handleResumeUpload}
                onParse={handleResumeParse}
                processing={isProcessing}
              />
              <div className="upload-info">
                <FaExclamationTriangle className="info-icon" />
                <p>
                  Upload your existing resume to automatically populate your profile.
                  We support PDF, DOC, DOCX, and TXT formats.
                </p>
              </div>
            </motion.div>
          </Modal>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default Profile;